import React, {Fragment, useCallback, useMemo, useState} from "react";
import {Box, Button, DialogActions, DialogContent, FormControl, MenuItem, Select, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import MaterialTable from "../material-table";
import {FILTER_DIM_SEPARATOR} from "./constants";
import {formatChartFilterValue, formatChartValue} from "./utils";

const Filter = ({value, values, setValue, filterDims, dimensionLabelMap, dimensionTypeMap, timeFormatMap}) => {
  const {t} = useTranslation();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleFilterOpen = () => {
    setIsFilterOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const valueObj = useMemo(() => {
    const valueSplit = value.split(FILTER_DIM_SEPARATOR);
    return filterDims.reduce((acc, dim, idx) => {
      acc[dim] = valueSplit[idx];
      return acc;
    }, {});
  }, [filterDims, value]);

  const getTooltip = useCallback(
    value => {
      return (
        <Box>
          {filterDims.map((dim, idx) => (
            <Box key={idx} sx={{margin: "4px 0"}}>
              <b>{dimensionLabelMap[dim]}</b>:{" "}
              <i>{formatChartValue(value[dim], dimensionTypeMap?.[dim], timeFormatMap)}</i>
            </Box>
          ))}
        </Box>
      );
    },
    [filterDims, dimensionLabelMap, dimensionTypeMap, timeFormatMap]
  );

  return (
    <Fragment>
      <FormControl
        sx={{
          display: "inline-block",
          verticalAlign: "bottom",
          maxWidth: "100%"
        }}
      >
        <Tooltip title={getTooltip(valueObj)}>
          <Select
            labelId="viewer-chart__filter__select-label"
            id="viewer-chart__filter__select"
            open={false}
            value={value}
            renderValue={value => formatChartFilterValue(value, filterDims, dimensionTypeMap, timeFormatMap)}
            sx={{
              fontSize: "14px",
              maxWidth: "100%"
            }}
            onOpen={handleFilterOpen}
          >
            {values.map(({id}) => (
              <MenuItem key={id} value={id}>
                {id}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
      </FormControl>

      <CustomDialog open={isFilterOpen} onClose={handleFilterClose}>
        <CustomDialogTitle onClose={handleFilterClose}>
          {t("modules.viewerChart.filter.dialog.title")}
        </CustomDialogTitle>
        <DialogContent>
          <Box sx={{height: "400px"}}>
            <MaterialTable
              data={values}
              idKey={"id"}
              columns={[
                {
                  field: "id",
                  title: "",
                  render: ({value}) =>
                    filterDims
                      .map(dim => formatChartValue(value[dim], dimensionTypeMap?.[dim], timeFormatMap))
                      .join(", "),
                  renderTooltip: ({value}) => getTooltip(value)
                }
              ]}
              onRowClick={(_e, rowData) => {
                setValue(rowData.id);
                handleFilterClose();
              }}
              getRowStyle={rowData => ({
                background: rowData.id === value ? "#fff9e5" : undefined
              })}
              hideHeader
              compact
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button id="viewer-chart__filter__dialog__close-button" onClick={handleFilterClose} color="primary">
            {t("commons.confirm.close")}
          </Button>
        </DialogActions>
      </CustomDialog>
    </Fragment>
  );
};

export default Filter;
